/**
 * Cmd/Ctrl-click navigation for Magic MD references.
 *
 * Supported sources:
 * - ::render[id]:: raw marks and pretty widgets
 * - ::use[id]{from="source"} raw from values and pretty source labels
 * - $ref{id.output.path} raw marks and pretty widgets
 */

import type { EditorView } from "@codemirror/view";
import { parseAssetRefPath, resolveDocumentAssetPath } from "@magic-md/core";
import { INLINE_RENDER_RE, REF_RE } from "@magic-md/core/patterns";
import {
    CONTENT_HEADER_RE,
    DEFINE_RE,
    FOR_HEADER_RE,
    IMPORT_RE,
    parseAttrs,
    USE_HEADER_RE,
} from "../patterns";
import { parseRefString } from "../ref/resolution";

interface NavigationDoc {
    lines: number;
    line(n: number): { text: string };
}

export function isMagicNavigationClick(event: MouseEvent): boolean {
    return (
        event.button === 0 &&
        (event.metaKey || event.ctrlKey) &&
        !event.altKey &&
        !event.shiftKey
    );
}

export function findMagicNavigationTargetLine(
    doc: NavigationDoc,
    targetId: string,
): number | null {
    const normalizedTarget = targetId.trim();
    if (!normalizedTarget || isNonNavigableTarget(normalizedTarget)) return null;

    for (let i = 1; i <= doc.lines; i++) {
        const trimmed = doc.line(i).text.trim();
        const headerId = getHeaderTargetId(trimmed);
        if (headerId === normalizedTarget) return i;
    }

    const namespace = getImportNamespaceFromTarget(normalizedTarget);
    if (!namespace) return null;

    for (let i = 1; i <= doc.lines; i++) {
        const trimmed = doc.line(i).text.trim();
        const importMatch = trimmed.match(IMPORT_RE);
        if (!importMatch) continue;
        const attrs = parseAttrs(importMatch[1]);
        if (attrs.ns === namespace) return i;
    }

    return null;
}

export function getRawMagicNavigationTargetAtOffset(
    lineText: string,
    offset: number,
): string | null {
    const renderMatch = findRegexMatchAtOffset(INLINE_RENDER_RE, lineText, offset);
    if (renderMatch?.[1]) return renderMatch[1];

    const refMatch = findRegexMatchAtOffset(REF_RE, lineText, offset);
    if (refMatch?.[0]) return getNavigationTargetFromRef(refMatch[0]);

    return getUseFromTargetAtOffset(lineText, offset);
}

export function getUseFromTargetAtOffset(
    lineText: string,
    offset: number,
): string | null {
    if (!USE_HEADER_RE.test(lineText.trim())) return null;

    const fromRe = /\bfrom="([^"]*)"/g;
    const fromMatch = fromRe.exec(lineText);
    if (!fromMatch) return null;

    const valueStart = fromMatch.index + 'from="'.length;
    const valueEnd = valueStart + fromMatch[1].length;
    if (offset < valueStart || offset > valueEnd) return null;
    return fromMatch[1];
}

export function getImportFromTargetAtOffset(
    lineText: string,
    offset: number,
): string | null {
    if (!IMPORT_RE.test(lineText.trim())) return null;

    const fromMatch = /\bfrom="([^"]*)"/.exec(lineText);
    if (!fromMatch || fromMatch.index === undefined) return null;

    const valueStart = fromMatch.index + 'from="'.length;
    const valueEnd = valueStart + fromMatch[1].length;
    if (offset < valueStart || offset > valueEnd) return null;
    return fromMatch[1];
}

export function getMagicNavigationTargetFromClick(
    event: MouseEvent,
    view: EditorView,
): string | null {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return null;

    const dataTarget = getDatasetNavigationTarget(target);
    if (dataTarget) return dataTarget;

    const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
    if (pos == null) return null;

    const line = view.state.doc.lineAt(pos);
    const offset = pos - line.from;
    const rawTarget = getRawMagicNavigationTargetAtOffset(line.text, offset);
    if (rawTarget) return rawTarget;

    if (target.closest(".cm-magic-use-head")) {
        return getUseFromTargetAtOffset(line.text, offset);
    }

    if (target.closest(".cm-magic-render") || target.closest(".cm-magic-ref")) {
        return getRawMagicNavigationTargetAtOffset(line.text, offset);
    }

    return null;
}

export function getMagicImportSourceFromClick(
    event: MouseEvent,
    view: EditorView,
): string | null {
    const target = event.target;
    if (target instanceof HTMLElement) {
        const importSourceEl = target.closest("[data-magic-import-source]");
        if (importSourceEl instanceof HTMLElement) {
            return importSourceEl.dataset.magicImportSource?.trim() || null;
        }
    }

    const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
    if (pos == null) return null;

    const line = view.state.doc.lineAt(pos);
    return getImportFromTargetAtOffset(line.text, pos - line.from);
}

export function resolveLocalImportSourcePath(
    documentPath: string | null | undefined,
    sourcePath: string | null | undefined,
): string | null {
    if (!documentPath || !sourcePath) return null;

    const assetMatch = sourcePath.match(/^\$ref\{([^}]*)\}$/);
    const assetRef = assetMatch ? parseAssetRefPath(assetMatch[1]) : null;
    if (assetRef && !assetRef.namespace) {
        return resolveDocumentAssetPath(documentPath, assetRef.relativePath);
    }
    if (sourcePath.startsWith("/") || /^[a-zA-Z]:\//.test(sourcePath)) {
        return sourcePath;
    }

    const lastSlash = documentPath.lastIndexOf("/");
    const dir = lastSlash >= 0 ? documentPath.substring(0, lastSlash) : ".";
    if (sourcePath.startsWith("./")) {
        return `${dir}/${sourcePath.substring(2)}`;
    }
    if (sourcePath.startsWith("../")) {
        const parentSlash = dir.lastIndexOf("/");
        const parentDir = parentSlash >= 0 ? dir.substring(0, parentSlash) : ".";
        return `${parentDir}/${sourcePath.substring(3)}`;
    }
    return `${dir}/${sourcePath}`;
}

function getDatasetNavigationTarget(target: HTMLElement): string | null {
    const navEl = target.closest("[data-magic-nav-target]");
    if (!(navEl instanceof HTMLElement)) return null;
    return navEl.dataset.magicNavTarget?.trim() || null;
}

function getHeaderTargetId(trimmedLine: string): string | null {
    const defineMatch = trimmedLine.match(DEFINE_RE);
    if (defineMatch) return defineMatch[1];

    const contentMatch = trimmedLine.match(CONTENT_HEADER_RE);
    if (contentMatch) return contentMatch[1];

    const forMatch = trimmedLine.match(FOR_HEADER_RE);
    if (forMatch) return forMatch[1];

    const useMatch = trimmedLine.match(USE_HEADER_RE);
    if (useMatch) return useMatch[1];

    return null;
}

function getNavigationTargetFromRef(refText: string): string | null {
    const parsed = parseRefString(refText);
    if (parsed) {
        return isNonNavigableTarget(parsed.definitionId) ? null : parsed.definitionId;
    }

    const refMatch = refText.match(/^\$ref\{([^}]+)\}$/);
    const refContent = refMatch?.[1]?.trim();
    if (!refContent) return null;
    const dotIdx = refContent.indexOf(".");
    const targetId = dotIdx === -1 ? refContent : refContent.slice(0, dotIdx);
    return isNonNavigableTarget(targetId) ? null : targetId;
}

function findRegexMatchAtOffset(
    regex: RegExp,
    lineText: string,
    offset: number,
): RegExpExecArray | null {
    const re = new RegExp(regex.source, "g");
    let match: RegExpExecArray | null;
    while ((match = re.exec(lineText))) {
        const from = match.index;
        const to = from + match[0].length;
        if (offset >= from && offset <= to) return match;
        if (match[0].length === 0) re.lastIndex++;
    }
    return null;
}

function getImportNamespaceFromTarget(targetId: string): string | null {
    const slashIdx = targetId.indexOf("/");
    if (slashIdx <= 0) return null;
    return targetId.slice(0, slashIdx);
}

function isNonNavigableTarget(targetId: string): boolean {
    return (
        targetId === "assets" ||
        targetId.endsWith("/assets") ||
        targetId.startsWith("assets/") ||
        targetId.startsWith("input/") ||
        targetId.startsWith("$")
    );
}
